import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { ArrowLeft, ExternalLink, Loader2 } from 'lucide-react';
import {
  fetchGrievanceUserPolicy,
  fetchGrievanceVendorPolicies,
  USER_CANCELLATION_POLICY_URL,
  type GrievancePolicyVersion,
  type GrievanceUserPolicyPayload,
  type GrievanceVendorPoliciesPayload,
} from '../../services/MasterAdmin/grievanceAdminService';
import {
  LEGAL_CATEGORY_LABELS,
  formatScopedVendorType,
  type LegalCategory,
} from '../../services/MasterAdmin/legalPolicyVersionsService';
import { vendorRoleMapping } from '../../services/MasterAdmin/vendorService';

const MARKDOWN_PROSE =
  'max-w-none text-sm leading-relaxed text-gray-900 dark:text-gray-100 [&_a]:break-words [&_a]:text-blue-600 [&_blockquote]:border-l-4 [&_blockquote]:border-gray-300 [&_blockquote]:pl-3 [&_code]:rounded [&_code]:bg-gray-100 [&_code]:px-1 [&_code]:py-px [&_code]:font-mono [&_code]:text-xs dark:[&_code]:bg-gray-800 [&_h1]:mb-3 [&_h1]:text-lg [&_h1]:font-bold [&_h2]:mt-4 [&_h2]:mb-2 [&_h2]:text-base [&_h2]:font-semibold [&_li]:my-1 [&_ol]:mb-3 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:mb-3 [&_pre]:overflow-x-auto [&_pre]:rounded-lg [&_pre]:bg-gray-100 [&_pre]:p-3 [&_pre]:dark:bg-gray-800 [&_table]:w-full [&_table]:border-collapse [&_th]:border [&_td]:border [&_th]:border-gray-300 [&_td]:border-gray-300 [&_th]:p-2 [&_td]:p-2 [&_tr]:dark:border-gray-600 dark:[&_th]:border-gray-600 dark:[&_td]:border-gray-600 [&_ul]:mb-3 [&_ul]:list-disc [&_ul]:pl-6';

function categoryLabel(cat: string) {
  return LEGAL_CATEGORY_LABELS[cat as LegalCategory] || cat.replace(/_/g, ' ');
}

const PolicyMarkdown: React.FC<{ content: string }> = ({ content }) => (
  <div className={MARKDOWN_PROSE}>
    <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
  </div>
);

const PolicyBlock: React.FC<{ policy: GrievancePolicyVersion; vendorRoleNum?: number | null }> = ({
  policy,
  vendorRoleNum,
}) => (
  <section className="rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-900">
    <div className="border-b border-gray-100 px-4 py-3 dark:border-gray-800">
      <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
        {categoryLabel(policy.category)}
      </h2>
      <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
        Version {policy.version}
        {policy.audience === 'VENDOR' && (
          <>
            {' '}
            · {formatScopedVendorType(policy.scopedVendorType)}
            {vendorRoleNum != null && vendorRoleMapping[String(vendorRoleNum)]
              ? ` (grievance vendor: ${vendorRoleMapping[String(vendorRoleNum)]})`
              : ''}
          </>
        )}
        {policy.title ? ` · ${policy.title}` : ''}
      </p>
    </div>
    <div className="px-4 py-4">
      <PolicyMarkdown content={policy.markdownContent} />
    </div>
  </section>
);

type GrievancePolicyPageProps = {
  mode: 'vendor' | 'user';
};

const GrievancePolicyPage: React.FC<GrievancePolicyPageProps> = ({ mode }) => {
  const { grievanceId } = useParams<{ grievanceId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [vendorData, setVendorData] = useState<GrievanceVendorPoliciesPayload | null>(null);
  const [userData, setUserData] = useState<GrievanceUserPolicyPayload | null>(null);

  const load = useCallback(async () => {
    if (!grievanceId) {
      setError('Missing grievance id.');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      if (mode === 'vendor') {
        const res = await fetchGrievanceVendorPolicies(grievanceId);
        setVendorData(res.data);
        setUserData(null);
      } else {
        const res = await fetchGrievanceUserPolicy(grievanceId);
        setUserData(res.data);
        setVendorData(null);
      }
    } catch (e: unknown) {
      const msg =
        typeof e === 'object' &&
        e !== null &&
        'response' in e &&
        typeof (e as { response?: { data?: { message?: string } } }).response?.data?.message ===
          'string'
          ? String((e as { response?: { data?: { message?: string } } }).response?.data?.message)
          : 'Failed to load policy.';
      setError(msg);
      setVendorData(null);
      setUserData(null);
    } finally {
      setLoading(false);
    }
  }, [grievanceId, mode]);

  useEffect(() => {
    void load();
  }, [load]);

  const title = mode === 'vendor' ? 'Vendor policies' : 'User cancellation & refund policy';
  const grievanceNumber = vendorData?.grievanceNumber ?? userData?.grievanceNumber;
  const category = vendorData?.category ?? userData?.category;
  const publicUrl = userData?.publicUrl || USER_CANCELLATION_POLICY_URL;

  return (
    <div className="mx-auto max-w-4xl space-y-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="mb-2 inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white">{title}</h1>
          {grievanceNumber && (
            <p className="mt-1 text-sm text-gray-500">
              Grievance <span className="font-mono">{grievanceNumber}</span>
              {category ? ` · ${category}` : ''}
            </p>
          )}
        </div>
        <Link
          to="/grievances"
          className="text-sm text-primary-600 hover:underline dark:text-primary-400"
        >
          All grievances
        </Link>
      </div>

      {loading && (
        <div className="flex justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
        </div>
      )}

      {error && (
        <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950/30 dark:text-red-300">
          {error}
        </p>
      )}

      {!loading && !error && mode === 'vendor' && vendorData && (
        <div className="space-y-4">
          {vendorData.vendorId && (
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Vendor ID: <span className="font-mono">{vendorData.vendorId}</span>
              {vendorData.vendor?.email ? ` · ${vendorData.vendor.email}` : ''}
            </p>
          )}
          {vendorData.policies.length === 0 ? (
            <p className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/20 dark:text-amber-200">
              No published vendor policies found for this grievance category
              {vendorData.vendorRoleNum != null
                ? ` and vendor role ${vendorRoleMapping[String(vendorData.vendorRoleNum)] || vendorData.vendorRoleNum}.`
                : '.'}{' '}
              Publish policies under Operations → Legal & policy versions (audience VENDOR).
            </p>
          ) : (
            vendorData.policies.map((p) => (
              <PolicyBlock key={p.id} policy={p} vendorRoleNum={vendorData.vendorRoleNum} />
            ))
          )}
        </div>
      )}

      {!loading && !error && mode === 'user' && userData && (
        <div className="space-y-4">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Same policy as on{' '}
            <a
              href={publicUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-primary-600 hover:underline dark:text-primary-400"
            >
              vedika.health/cancellation-refund-policy
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          </p>
          {userData.policy ? (
            <PolicyBlock policy={userData.policy} />
          ) : (
            <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
              <iframe
                title="User cancellation and refund policy"
                src={publicUrl}
                className="h-[min(80vh,720px)] w-full bg-white"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export const GrievanceVendorPolicyPage: React.FC = () => <GrievancePolicyPage mode="vendor" />;
export const GrievanceUserPolicyPage: React.FC = () => <GrievancePolicyPage mode="user" />;
export default GrievancePolicyPage;
